import React, { useEffect, useMemo, useRef, useState } from "react"
import styles from "./Chat.module.scss"
import pretzelIcon from "./logo-square.png"
import axios from "axios"
import Files from "./Files/Files"
import { UserInterface } from "../../../App"
import { API_ROUTE, getFileColor } from "../../../utils"
import FlashCards from "./FlashCards/FlashCards"
import MessageBar from "../../MessageBar/MessageBar"
import { ChatModeInterface } from "../../Home/Home"
import toast from "react-hot-toast"
import WebSearch from "./WebSearch/WebSearch"
import Welcome from "./Welcome/Welcome"
import { generateID } from "./Files/helpers"

type MessageType = "text" | "flashcards" | "websearch" | "files" | "summary"
type Role = "user" | "assistant"

interface ChatMessage {
    id: string
    role: "user" | "assistant"
    time: string
    type: MessageType
    content: string
    context?: any[]
    payload?: any
}

const Chat: React.FC<{
    appSpaces: any
    activeAppSpace: any
    setActiveAppSpace: any
    newAppSpace: any
    rawContext: string
    activeSession: any
    setUser: any
    user: UserInterface
    send?: string
    createFile: string
    chatMode: ChatModeInterface["chatMode"]
    setChatMode: React.Dispatch<React.SetStateAction<ChatModeInterface["chatMode"]>>
    messages?: any
    setMessages?: React.Dispatch<React.SetStateAction<any>>
    sessions?: any
    fileTree?: any
    setFileTree?: React.Dispatch<React.SetStateAction<any>>
}> = ({
    appSpaces,
    rawContext,
    activeAppSpace,
    setActiveAppSpace,
    newAppSpace,
    activeSession,
    setUser,
    user,
    send,
    createFile,
    chatMode,
    setChatMode,
    messages,
    setMessages = (f: any) => f, // Default to a no-op function if not provided
    sessions,
    fileTree,
    setFileTree,
}) => {
    const formatCurrentTime = (time: string) => {
        const date = new Date(time)
        const hours = date.getHours()
        const minutes = date.getMinutes()
        const ampm = hours >= 12 ? "PM" : "AM"
        const formattedHours = hours % 12 || 12
        const formattedMinutes = minutes < 10 ? "0" + minutes : minutes
        const currentTime = `${formattedHours}:${formattedMinutes} ${ampm}`
        return currentTime
    }

    const messageRefs = useRef(new Map<string, HTMLElement>())
    const messagesContainer = useRef<HTMLDivElement>(null)

    const [pretzelTyping, setPretzelTyping] = useState(false)
    const [flashcardsData, setFlashcardsData] = useState("")
    const [webSearchResults, setWebSearchResults] = useState() as any

    const [uploading, setUploading] = useState(false)

    const [input, setInput] = useState("")

    const legacyAutoScroll = () => {
        if (messagesContainer.current) {
            // messagesContainer.current.scrollTop = messagesContainer.current.scrollHeight

            const scrollHeight = messagesContainer.current.scrollHeight
            const clientHeight = messagesContainer.current.clientHeight
            const scrollTop = messagesContainer.current.scrollTop
            const newScrollTop = scrollHeight - clientHeight > 600 ? scrollTop + 600 : scrollHeight
            messagesContainer.current.scrollTo({
                top: newScrollTop,
                behavior: "smooth",
            })
        }
    }

    const smartScroll = () => {
        if (messagesContainer.current) {
            const sortedMessages = Object.entries(messages)
                .sort((a: any, b: any) => a[1].time - b[1].time)
                .map(([id, message]) => ({ id, ...(message as any) }))

            const lastMessageId = Object.keys(sortedMessages).pop()

            if (lastMessageId && messageRefs.current.has(lastMessageId)) {
                const lastMessageElement = messageRefs.current.get(lastMessageId)
                if (lastMessageElement) {
                    lastMessageElement.scrollIntoView({ behavior: "smooth" })
                }
            } else {
                legacyAutoScroll()
            }
        }
    }

    const roleToName = (role: Role) => {
        switch (role) {
            case "user":
                return user.name
            case "assistant":
                return "Pretzel"
            default:
                return ""
        }
    }

    // --- Append a new message to chat ---
    const appendMessage = (role: Role, text: string, context?: []) => {
        const uuid = generateID(role)

        const message = {
            id: uuid,
            role,
            time: Date.now(),
            content: text,
            context: context || [],
        }

        setMessages((prev: any) => ({
            ...prev,
            [uuid]: message,
        }))

        setTimeout(() => smartScroll(), 150)
        return message
    }

    // --- GPT API Call Wrapper ---
    const sendToGpt = async ({
        history,
        writeToMessage,
        callback,
    }: {
        history?: { [key: string]: { role: string; content: string } }
        writeToMessage?: string
        callback?: (chunks: string) => void
    }) => {
        console.log(String(activeSession?.id))
        const response = (await fetch(`${API_ROUTE}/utils/gpt`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                messages: history,
                stream: true,
                userId: user?._id,
                authToken: user?.authToken,
                chatId: String(activeSession?.id),
            }),
        })) as any

        if (!response.body) throw new Error("No response body")

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let chunks = ""

        while (true) {
            const { done, value } = await reader.read()
            if (done) {
                console.log("Stream finished")
                break
            }

            try {
                const chunk = decoder.decode(value, { stream: true })
                chunks += chunk

                if (writeToMessage) {
                    setMessages((prev: any) => ({
                        ...prev,
                        [writeToMessage]: {
                            ...prev[writeToMessage],
                            content: prev[writeToMessage].content + chunk,
                        },
                    }))
                }
            } catch (error) {
                console.error("Error parsing chunk:", error)
            }
        }

        if (callback) callback(chunks)
    }

    const handleRAG = async (
        messages: any[],
        user: UserInterface,
        chatMode: ChatModeInterface["chatMode"]
    ) => {
        const reqPayload = {
            messages,
            user: user || null,
        } as any

        if (chatMode === "asking open file" && activeAppSpace.fileId) {
            reqPayload.file_id = activeAppSpace.fileId
        }

        if (chatMode === "asking open file" && !activeAppSpace.fileId)
            return { response: "No active file found. Please open a file first." }

        const response = await axios.post(`${API_ROUTE}/utils/rag`, reqPayload)

        if (response.data?.noResults) {
            // nothing
            return {
                response: `${response.data?.noResults} <br /><br /> <i>We might still be processing your files. Please wait and try again later.</i>`,
            }
        } else if (response.data?.context) {
            // return context, will now await streamed response from GPT
            return {
                response: "",
                context: response.data?.context || [],
            }
        } else return { response: "Please try again later." } // fallback response
    }

    // --- Flashcards Handler ---
    const handleFlashcardsRequest = async (history: any) => {
        const context = rawContext.slice(0, 4000).replace(/<[^>]*>/g, "")

        // no rag, and no streaming, return the entire response at once
        sendToGpt({
            history: {
                ...history,
                [Date.now().toString()]: {
                    role: "system",
                    content: `Create flashcards based on the user's active document. Format flashcards as follows to be parsed by my server:
    <item>Q: Question 1 / A: Answer 1</item><item>Q: Question 2 / A: Answer 2</item>
    
    ${context}
    `,
                },
            },
            callback: (chunks: string) => {
                if (chunks.includes("<item>")) {
                    setFlashcardsData(chunks)
                } else {
                    toast.error("Failed to generate flashcards. Please try again later.")
                }
                setTimeout(() => {
                    // timeout needed, due to the way flashcards are rendered
                    legacyAutoScroll()
                }, 200)
            },
        })
    }

    // --- Summary Handler ---
    const handleSummaryRequest = async (history: any) => {
        const context = rawContext.slice(0, 4000).replace(/(<([^>]+)>)/gi, "")

        const messageId = appendMessage("assistant", "").id
        sendToGpt({
            history: {
                ...history,
                [Date.now().toString()]: {
                    role: "system",
                    content: `Summarize the user's active document in a concise manner:
${context}`,
                },
            },
            writeToMessage: messageId,
        })
    }

    // --- Standard Chat Handler ---
    const handleStandardMessage = async (text: string, history: any) => {
        const historyAsArray = Object.values(history).filter(
            (msg: any) => msg.content !== "files" && msg.content !== "flashcards"
        )

        if (chatMode === "asking all files" || chatMode === "asking open file") {
            const reply = await handleRAG(historyAsArray, user, chatMode)

            const messageId = appendMessage("assistant", reply.response, reply.context).id

            if (!reply.response) {
                // 3. Prepare the final prompt for the AI
                const formattedContext = reply.context
                    .map((item: any) => `Context from file "${item.filename}":\n${item.chunk}`)
                    .join("\n\n---\n\n") // Join with a clear separator

                const systemMessage = {
                    role: "system",
                    content: `You are a helpful assistant. Use the following context from the user's files to answer their question.
            ---
            CONTEXT:
            ${formattedContext}
            ---
            Based on the context above, answer the user's question.`,
                }

                // 4. Generate the final response from the GPT
                sendToGpt({
                    history: { [Date.now().toString()]: systemMessage, ...history },
                    writeToMessage: messageId,
                })
            }
        } else if (chatMode === "asking the web") {
            // The backend is called to retrieve web search results based on the conversation.
            const response = (await axios.post(`${API_ROUTE}/utils/gpt-web-search`, {
                messages: historyAsArray,
            })) as any

            const data = response.data
            // Validate the response from the backend.
            if (!data?.searchQuery || !data?.sources || !data?.summaries) {
                appendMessage("assistant", "No results found. Please try a different query.")
                return
            }

            // 1. Format the retrieved context for the AI model.
            // Each source is numbered and paired with its content summary.
            const formattedContext = data.sources
                .map(
                    (source: string, index: number) =>
                        `Source [${index + 1}]: ${source}\nContent: ${data.summaries[index]}`
                )
                .join("\n\n---\n\n") // Use a clear separator for readability.

            // 2. Engineer the system prompt to guide the AI's response.
            // This prompt instructs the AI to use the provided context and cite its sources.
            const systemMessage = {
                role: "system",
                content: `You are a helpful AI assistant that answers user questions based on web search results.
        ---
        CONTEXT FROM WEB SEARCH:
        ${formattedContext}
        ---
        Based on the context above, provide a comprehensive answer to the user's question. You MUST cite the sources you use with the notation [1], [2], etc., corresponding to the source number.`,
            }

            console.log("System message:", systemMessage)

            // 3. Append a placeholder message to the UI.
            // This message will display the sources to the user and will be updated with the AI's response.
            const messageId = appendMessage(
                "assistant",
                "", // The AI will stream its response into this empty string.
                data.sources.map((url: string) => ({
                    chunk: "", // The chunk is not needed here, but the filename (URL) is used for the source display.
                    filename: url,
                }))
            ).id

            // 4. Assemble and send the augmented prompt to GPT for final answer generation.
            // The response will be streamed into the message placeholder created above.
            sendToGpt({
                history: { [Date.now().toString()]: systemMessage, ...history },
                writeToMessage: messageId,
            })
        } else if (chatMode === "web search only") {
            const response = await axios.post(`${API_ROUTE}/utils/web-search`, {
                query: text,
            })

            if (response) {
                console.log("Web search response:", response)
                setWebSearchResults(response)
                appendMessage("assistant", "websearch")
            } else {
                appendMessage("assistant", "No results found. Please try a different query.")
            }
        } else {
            const messageId = appendMessage("assistant", "").id

            sendToGpt({
                history: history,
                writeToMessage: messageId,
            })
        }
    }

    // --- Main Dispatcher ---
    const sendMessage = async (text: string) => {
        if (!text) return
        const messageAppended = appendMessage("user", text)

        if (text.toLowerCase() === "files") return

        const cmd = text.toLowerCase().replace(/\s+/g, "")

        try {
            setPretzelTyping(true)
            if (cmd === "flashcards") {
                await handleFlashcardsRequest({
                    ...messages,
                    [messageAppended.id]: messageAppended,
                })
            } else if (cmd === "summarize") {
                await handleSummaryRequest({
                    ...messages,
                    [messageAppended.id]: messageAppended,
                })
            } else {
                await handleStandardMessage(text, {
                    ...messages,
                    [messageAppended.id]: messageAppended,
                })
            }
        } catch (error) {
            console.error("Error sending message:", error)
            appendMessage("assistant", "Please try again later.")
        } finally {
            setPretzelTyping(false)
        }
    }

    useEffect(() => {
        if (messagesContainer.current) {
            messagesContainer.current.scrollTop = messagesContainer.current.scrollHeight
        }
    }, [sessions])

    useEffect(() => {
        if (send) {
            sendMessage(send)
            // eslint-disable-next-line react-hooks/exhaustive-deps
            send = undefined
        }
    }, [send])

    interface ContextItem {
        filename: string
        chunk: string
    }

    interface ContextProps {
        context: ContextItem[]
    }

    const Context: React.FC<ContextProps> = ({ context }) => {
        const [expanded, setExpanded] = useState(false)

        // Memoize these calculations as they only depend on the 'context' prop
        const uniqueFilesReferenced = useMemo(() => {
            return new Set(context.map((item) => item.filename)).size
        }, [context])

        const allFilesAreUrls = useMemo(() => {
            return context.every((item) => item.filename.startsWith("http"))
        }, [context])

        // Helper function for rendering file type text
        const getFileTypeLabel = useMemo(() => {
            if (allFilesAreUrls) {
                return "websites"
            }
            return uniqueFilesReferenced === 1 ? "file" : "files"
        }, [allFilesAreUrls, uniqueFilesReferenced])

        return (
            <div className={styles.context} style={{ height: expanded ? "auto" : "80px" }}>
                <div className={styles.contextCollapse} onClick={() => setExpanded(!expanded)}>
                    <i className="material-symbols-rounded">
                        {allFilesAreUrls ? "link" : "folder"}
                    </i>
                    {uniqueFilesReferenced} {getFileTypeLabel} used
                    {expanded ? "" : " - Click to expand"}
                </div>

                {context.map((item, idx) => (
                    <div className={styles.item} key={idx}>
                        {item.chunk && <p>{item.chunk}</p>}
                        {allFilesAreUrls ? (
                            <a
                                href={item.filename}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.webLink}>
                                <i className="material-symbols-rounded">link</i>[{idx + 1}].{" "}
                                {item.filename}
                            </a>
                        ) : (
                            <div className={styles.file}>
                                <div
                                    className={styles.icon}
                                    style={{ background: getFileColor("pdf") }}></div>
                                {item.filename}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        )
    }

    function renderMessageContent(message: ChatMessage) {
        switch (message.content) {
            case "files":
                return (
                    <Files
                        newAppSpace={newAppSpace}
                        setActiveAppSpace={setActiveAppSpace}
                        setUser={setUser}
                        user={user}
                        createFile={createFile}
                        uploading={uploading}
                        setUploading={setUploading}
                        fileTree={fileTree}
                        setFileTree={setFileTree}
                    />
                )
            case "flashcards":
                return <FlashCards message={flashcardsData} />
            case "websearch":
                return (
                    <WebSearch
                        webSearchResults={webSearchResults}
                        setWebSearchResults={setWebSearchResults}
                    />
                )
            default:
                return message.role === "assistant" ? (
                    <p
                        className={styles.content}
                        dangerouslySetInnerHTML={{
                            __html: message.content.replace(/(?:\r\n|\r|\n)/g, "<br>"),
                        }}
                    />
                ) : (
                    <div className={styles.content}>{message.content}</div>
                )
        }
    }

    return (
        <div className={styles.Chat}>
            <div className={styles.header}>
                <div className={styles.group}>
                    <p>ASSISTANT THREAD</p>
                    <div className="flex">
                        <i className="material-symbols-rounded">chat</i>
                        {activeSession?.name}
                    </div>
                </div>
                <div className={styles.group} style={{ textAlign: "right" }}>
                    <p className="flex">
                        <div>
                            LLM <span>GPT-4o</span>
                        </div>{" "}
                        <div>
                            RAG k <span>10</span>
                        </div>
                    </p>
                    <p>
                        Web Access <span>Brave Search</span>
                    </p>
                </div>
            </div>
            <div ref={messagesContainer} className={styles.container}>
                {Object.entries(messages).length ? (
                    <div className={styles.messages}>
                        {Object.entries(messages)
                            .filter(([, message]: any) => message.role !== "system")
                            .sort((a: any, b: any) => a[1].time - b[1].time)
                            .map(([id, message]: any, index) => {
                                const isPretzel = message.role === "assistant"

                                return (
                                    <div
                                        key={id}
                                        ref={(el) => {
                                            if (el) {
                                                messageRefs.current.set(id, el)
                                            } else {
                                                messageRefs.current.delete(id) // Clean up on unmount
                                            }
                                        }}
                                        className={styles.message}>
                                        {isPretzel ? (
                                            <img
                                                src={pretzelIcon}
                                                width={40}
                                                height={40}
                                                alt="icon"
                                            />
                                        ) : (
                                            <div className={styles.profile}>
                                                {roleToName(message.role)?.slice(0, 2)}
                                            </div>
                                        )}

                                        <div>
                                            <div className="flex">
                                                <p className={styles.name}>
                                                    {roleToName(message.role)}
                                                </p>
                                                <p className={styles.time}>
                                                    {formatCurrentTime(message.time)}
                                                </p>
                                            </div>

                                            {renderMessageContent(message)}

                                            {message.context?.length > 0 && (
                                                <Context context={message.context} />
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                    </div>
                ) : (
                    <Welcome
                        sendMessage={sendMessage}
                        chatMode={chatMode}
                        setChatMode={setChatMode}
                    />
                )}
            </div>

            <div style={{ opacity: pretzelTyping ? 1 : 0 }} className={styles.pretzelTyping}>
                <div style={{ scale: "0.7" }} className="loader" />
                <p>Pretzel is typing...</p>
            </div>

            <div style={{ margin: "0 1rem 0 2rem" }}>
                <MessageBar
                    input={input}
                    setInput={setInput}
                    disabled={pretzelTyping || uploading}
                    callback={sendMessage}
                    chatMode={chatMode}
                    setChatMode={setChatMode}
                />
            </div>

            {appSpaces?.length > 0 && (
                <div className={styles.tray}>
                    <div className={styles.demo}>
                        <span onClick={() => sendMessage("files")}>Files</span>
                        <span onClick={() => sendMessage("flashcards")}>Flashcards</span>
                        <span onClick={() => sendMessage("summarize")}>Summarize</span>
                    </div>
                </div>
            )}
            <br />
        </div>
    )
}

export default Chat
